import * as React from "react"
import { Calendar } from "./calendar"
import { Popover, PopoverContent, PopoverTrigger } from "./popover"
import { Button } from "./button"
import { Input } from "./input"
import { cn } from "../../lib/utils"
import { CalendarIcon } from "lucide-react"
import { format, parse, isValid } from "date-fns"

// Helper function to convert any value to a date string
const valueToDateString = (val) => {
  if (val == null) return ""
  if (typeof val === 'string') return val
  // Handle Date objects - format them properly
  if (val instanceof Date && isValid(val)) {
    return format(val, 'MM/dd/yyyy')
  }
  // For other types, try to convert but return empty if it results in [object Object]
  const strVal = String(val)
  if (strVal === '[object Object]') return ""
  return strVal
}

const DatePickerInput = React.forwardRef(({ 
  className, 
  value, 
  onChange, 
  onBlur: externalOnBlur,
  placeholder = "MM/DD/YYYY",
  disabled,
  readOnly,
  minDate,
  maxDate,
  ...props 
}, ref) => {
  const [open, setOpen] = React.useState(false)
  // Ensure initial value is a string, handling Date objects, numbers, null, undefined
  const [inputValue, setInputValue] = React.useState(valueToDateString(value))
  const [isValidDate, setIsValidDate] = React.useState(true)

  // Convert value to Date object for calendar
  const getDateFromValue = (val) => {
    if (!val) return undefined
    
    // Handle Date objects directly
    if (val instanceof Date && isValid(val)) {
      return val
    }
    
    // Ensure val is a string before calling string methods
    // This handles cases where val could be a number or other type
    const strVal = typeof val === 'string' ? val : String(val)
    
    // Skip if it's [object Object]
    if (strVal === '[object Object]') return undefined
    
    // Handle different date formats
    let dateObj
    if (strVal.includes('/')) {
      // MM/DD/YYYY format
      dateObj = parse(strVal, 'MM/dd/yyyy', new Date())
    } else if (strVal.includes('-')) {
      // YYYY-MM-DD format (from type="date" inputs)
      dateObj = parse(strVal, 'yyyy-MM-dd', new Date())
    } else {
      return undefined
    }
    
    return isValid(dateObj) ? dateObj : undefined
  }

  const selectedDate = getDateFromValue(value)

  // Validate date format (MM/DD/YYYY) and constraints
  const validateDateFormat = (dateString) => {
    if (!dateString) return true // Empty is valid
    
    // Check basic format MM/DD/YYYY
    const dateRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/
    if (!dateRegex.test(dateString)) return false
    
    // Check if it's a valid date
    const parsedDate = parse(dateString, 'MM/dd/yyyy', new Date())
    if (!isValid(parsedDate)) return false
    
    // Check min/max constraints
    if (minDate && parsedDate < minDate) return false
    if (maxDate && parsedDate > maxDate) return false
    
    return true
  }

  // Auto-format date: Convert MMDDYYYY to MM/DD/YYYY
  const autoFormatDate = (value) => {
    if (!value) return value
    
    // Remove any existing slashes and spaces
    const digitsOnly = value.replace(/[^0-9]/g, '')
    
    // If exactly 8 digits, format as MM/DD/YYYY
    if (digitsOnly.length === 8) {
      const mm = digitsOnly.substring(0, 2)
      const dd = digitsOnly.substring(2, 4)
      const yyyy = digitsOnly.substring(4, 8)
      return `${mm}/${dd}/${yyyy}`
    }
    
    // If 7 digits, assume leading zero is missing (e.g., 1011989 -> 01/01/1989)
    if (digitsOnly.length === 7) {
      const mm = '0' + digitsOnly.substring(0, 1)
      const dd = digitsOnly.substring(1, 3)
      const yyyy = digitsOnly.substring(3, 7)
      return `${mm}/${dd}/${yyyy}`
    }
    
    // Return as-is if not 7 or 8 digits
    return value
  }

  // Handle manual input change
  const handleInputChange = (e) => {
    const newValue = e.target.value
    setInputValue(newValue)
    
    // Only validate if the user has entered a complete date (10 characters: MM/DD/YYYY)
    // or if the field is empty (which is valid)
    if (newValue.length === 0 || newValue.length === 10) {
      const isValid = validateDateFormat(newValue)
      setIsValidDate(isValid)
    } else {
      // For partial dates, assume valid (don't show error while typing)
      setIsValidDate(true)
    }
    
    if (onChange) {
      onChange(e) // Pass the event to parent
    }
  }
  
  // Handle blur event to auto-format dates
  const handleBlur = (e) => {
    const currentValue = e.target.value
    
    if (currentValue) {
      const formattedValue = autoFormatDate(currentValue)
      
      // If formatting changed the value, update it
      if (formattedValue !== currentValue) {
        setInputValue(formattedValue)
        
        // Validate the formatted date
        const isValid = validateDateFormat(formattedValue)
        setIsValidDate(isValid)
        
        // Create a synthetic event with the formatted value
        const syntheticEvent = {
          target: { value: formattedValue }
        }
        
        if (onChange) {
          onChange(syntheticEvent)
        }
        
        // DON'T call external onBlur when auto-formatting
        // The parent's validation will run on the next interaction or when clicking Complete
        // This prevents validation from running on stale state
        // The internal DatePickerInput validation (isValidDate) still provides immediate feedback
        
        return // Exit early - don't run external validation
      } else {
        // Value didn't change, just validate it
        const isValid = validateDateFormat(currentValue)
        setIsValidDate(isValid)
      }
    } else {
      // Empty value - clear validation error
      setIsValidDate(true)
    }
    
    // Call external onBlur ONLY if the value didn't need formatting
    // This means the user entered a properly formatted date, so validation should work correctly
    if (externalOnBlur) {
      externalOnBlur(e)
    }
  }

  // Handle calendar date selection
  const handleDateSelect = (date) => {
    if (date) {
      const formattedDate = format(date, 'MM/dd/yyyy')
      setInputValue(formattedDate)
      setIsValidDate(true)
      
      // Create a synthetic event for consistency
      const syntheticEvent = {
        target: { value: formattedDate }
      }
      
      if (onChange) {
        onChange(syntheticEvent)
      }
    }
    setOpen(false)
  }

  // Update input value when value prop changes
  React.useEffect(() => {
    // Ensure value is converted to string, handling null, undefined, Date objects, numbers, etc.
    const newValue = valueToDateString(value)
    setInputValue(newValue)
    
    // Only validate if it's a complete date or empty
    // This prevents validation errors while the parent is still updating
    if (newValue.length === 0 || newValue.length === 10) {
      setIsValidDate(validateDateFormat(newValue))
    } else {
      setIsValidDate(true) // Don't show error for incomplete dates from parent
    }
  }, [value])

  return (
    // Jun 2026 — wrapper must NOT receive the caller's className: callers
    // pass fixed heights (h-9) meant for the Input, which froze the wrapper
    // at 36px and made the inline format error below overflow/overlap the
    // next grid row (user-reported on the Fulfillment tab). className is
    // already applied to the Input itself.
    <div className="relative">
      <Popover open={open} onOpenChange={setOpen}>
        <div className="flex">
          <Input
            ref={ref}
            type="text"
            placeholder={placeholder}
            value={inputValue}
            onChange={handleInputChange}
            onBlur={handleBlur}
            disabled={disabled}
            readOnly={readOnly}
            className={cn(
              "pr-10",
              !isValidDate && "border-red-500 focus-visible:ring-red-500",
              className
            )}
            {...props}
          />
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
              disabled={disabled || readOnly}
            >
              <CalendarIcon className="h-4 w-4" />
              <span className="sr-only">Open calendar</span>
            </Button>
          </PopoverTrigger>
        </div>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={handleDateSelect}
            disabled={(date) => {
              if (disabled) return true
              if (minDate && date < minDate) return true
              if (maxDate && date > maxDate) return true
              return false
            }}
            initialFocus
          />
        </PopoverContent>
      </Popover>
      {!isValidDate && (
        <p className="mt-0.5 text-xs text-red-500 break-words">
          Please enter a valid date in MM/DD/YYYY format
        </p>
      )}
    </div>
  )
})

DatePickerInput.displayName = "DatePickerInput"

export { DatePickerInput }